import React from 'react'
import styles from './CalendlyEmbed.module.css'

export interface CalendlyEmbedProps {
  /**
   * Client's Calendly URL
   */
  url: string

  /**
   * Callback when booking is completed
   */
  onBookingComplete: (eventId: string) => void

  /**
   * Prefill data for the form
   */
  prefill?: {
    name?: string
    email?: string
    phone?: string
  }
}

// Type definition for Calendly postMessage events
interface CalendlyMessageData {
  event: string
  payload?: {
    event?: {
      uri?: string
    }
  }
}

/**
 * Build Calendly URL with prefill parameters
 */
const buildCalendlyUrl = (baseUrl: string, prefill?: { name?: string; email?: string; phone?: string }): string => {
  if (!prefill) return baseUrl

  const url = new URL(baseUrl)

  // Calendly prefill parameters
  if (prefill.name) {
    url.searchParams.set('name', prefill.name)
  }
  if (prefill.email) {
    url.searchParams.set('email', prefill.email)
  }
  if (prefill.phone) {
    // Format phone for Calendly (remove special characters)
    const formattedPhone = prefill.phone.replace(/\D/g, '')
    url.searchParams.set('a1', formattedPhone) // Calendly uses 'a1' for custom phone field
  }

  return url.toString()
}

/**
 * Calendly inline embed component
 * Embeds Calendly booking widget via iframe
 */
export const CalendlyEmbed: React.FC<CalendlyEmbedProps> = ({ url, onBookingComplete, prefill }) => {
  const [isLoading, setIsLoading] = React.useState(true)
  const [hasError, setHasError] = React.useState(false)

  // Build URL with prefill data
  const calendlyUrl = React.useMemo(() => buildCalendlyUrl(url, prefill), [url, prefill])

  React.useEffect(() => {
    // Listen for Calendly events
    const handleMessage = (e: MessageEvent<CalendlyMessageData>) => {
      if (
        e.origin === 'https://calendly.com' &&
        e.data?.event === 'calendly.event_scheduled' &&
        e.data?.payload?.event?.uri
      ) {
        onBookingComplete(e.data.payload.event.uri)
      }
    }

    window.addEventListener('message', handleMessage as EventListener)
    return () => window.removeEventListener('message', handleMessage as EventListener)
  }, [onBookingComplete])

  const handleLoad = () => {
    setIsLoading(false)
  }

  const handleError = () => {
    setIsLoading(false)
    setHasError(true)
  }

  return (
    <div className={styles.calendlyEmbed} data-testid="calendly-embed">
      {isLoading && (
        <div className={styles.loading} data-testid="loading-state">
          <div className={styles.spinner} />
          <p>Loading calendar...</p>
        </div>
      )}

      {hasError && (
        <div className={styles.error} data-testid="error-state">
          <p>Unable to load calendar. Please try again later.</p>
        </div>
      )}

      <iframe
        src={calendlyUrl}
        width="100%"
        height="100%"
        frameBorder="0"
        title="Calendly scheduling page"
        onLoad={handleLoad}
        onError={handleError}
        className={`${styles.iframe} ${isLoading ? styles.hidden : ''}`}
        data-testid="calendly-iframe"
      />
    </div>
  )
}
